import { useState, useCallback } from 'react';
import { MetricCards } from '@/components/analytics/MetricCards';
import { AnalyticsCharts } from '@/components/analytics/Charts';
import { useAnalytics } from '@/hooks/useRecords';
import { useLanguage } from '@/context/LanguageContext';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import type { DateFilter } from '@/types';
import { FileCode, Database, ArrowRight, Settings, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { CarSalesAnalytics } from './CarSalesAnalytics';

interface AnalyticsPageProps {
  tableName?: string;
}

// Check if this is the default alsalama_call_records table
const isCallRecordsTable = (tableName: string) => {
  return tableName === 'alsalama_call_records';
};

// Check if this is the car_sales table (flexible matching)
const isCarSalesTable = (tableName: string) => {
  // Match various possible names for car sales
  const lowerName = tableName.toLowerCase();
  return lowerName === 'car_sales' || 
         lowerName.includes('car_sales') || 
         lowerName === 'car sales' ||
         lowerName.includes('car sales');
};

export function AnalyticsPage({ tableName = 'alsalama_call_records' }: AnalyticsPageProps) {
  const { t } = useLanguage();
  
  // Only fetch analytics data for alsalama_call_records
  const [dateFilter, setDateFilter] = useState<DateFilter>({ preset: 'all', from: null, to: null });
  const { data, loading } = useAnalytics(tableName, dateFilter);

  const handlePresetChange = useCallback((preset: DateFilter['preset']) => {
    setDateFilter({ preset, from: null, to: null });
  }, []);

  const handleCustomDateChange = useCallback((from: string | null, to: string | null) => {
    setDateFilter({ preset: 'custom', from, to });
  }, []);

  const isCustomFilter = dateFilter.preset === 'custom' || dateFilter.from || dateFilter.to;

  // Show custom analytics for car_sales table
  if (isCarSalesTable(tableName)) {
    return <CarSalesAnalytics tableName={tableName} />;
  }

  // If not alsalama_call_records or car_sales, show custom analytics placeholder
  if (!isCallRecordsTable(tableName)) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('nav.analytics')} - {tableName}
            </h1>
            <p className="text-muted-foreground mt-1">
              Custom analytics for {tableName} table
            </p>
          </div>
        </div>

        {/* Custom Analytics Placeholder */}
        <div className="bg-card border rounded-xl p-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <FileCode className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Custom Analytics Coming Soon
            </h2>
            <p className="text-muted-foreground mb-6">
              This table doesn't have custom analytics yet. You can create a custom analytics 
              component and connect it to this table.
            </p>
            
            {/* Template Info */}
            <div className="bg-muted/50 rounded-lg p-4 text-left mb-6">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                How to Add Custom Analytics
              </h3>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Create a new analytics component in <code className="bg-muted px-1.5 py-0.5 rounded text-xs">src/components/analytics/</code></li>
                <li>Use the template from <code className="bg-muted px-1.5 py-0.5 rounded text-xs">CustomAnalyticsTemplate.tsx</code></li>
                <li>Import it in AnalyticsPage.tsx</li>
                <li>Add your table name to the custom analytics config</li>
              </ol>
            </div>

            <Button className="gap-2">
              <Settings className="h-4 w-4" />
              Go to Admin Settings
            </Button>
          </div>
        </div>

        {/* Quick Stats - Generic for any table */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Database className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Table Name</p>
                <p className="text-lg font-semibold">{tableName}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileCode className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-lg font-semibold">Custom Analytics</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <ArrowRight className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Next Step</p>
                <p className="text-lg font-semibold">Configure in Admin</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original alsalama_call_records analytics
  const metrics = data ? {
    totalCalls: data.totalCalls,
    resolvedCalls: data.resolvedCalls,
    pendingCalls: data.pendingCalls,
    avgDuration: data.avgDuration,
    
    // New metrics
    highUrgencyRate: data.highUrgencyRate || 0,
    atRiskCount: data.atRiskCount || 0,
    topPeakHour: data.peakHours && data.peakHours.length > 0
      ? data.peakHours.sort((a, b) => b.count - a.count)[0]
      : undefined,
  } : {
    totalCalls: 0,
    resolvedCalls: 0,
    pendingCalls: 0,
    avgDuration: 0,
    highUrgencyRate: 0,
    atRiskCount: 0,
    topPeakHour: undefined,
  };

  return (
    <div className="space-y-6">
      {/* Date Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">{t('filter.quick')}</span>
        {(['all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as const).map((preset) => (
          <button
            key={preset}
            onClick={() => handlePresetChange(preset)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              dateFilter.preset === preset && !isCustomFilter
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            {t(`filter.${preset}`)}
          </button>
        ))}
        <div className="w-px h-6 bg-border mx-2" />
        <DateRangePicker
          from={dateFilter.from}
          to={dateFilter.to}
          onChange={handleCustomDateChange}
          className="w-64"
          placeholder="Custom range..."
        />
      </div>

      {/* Metrics */}
      <MetricCards metrics={metrics} loading={loading} />

      {/* Charts */}
      <AnalyticsCharts data={data || {
        totalCalls: 0,
        resolvedCalls: 0,
        pendingCalls: 0,
        avgDuration: 0,
        byDate: {},
        byHour: {},
        byDayOfWeek: {},
        byCategory: {},
        byServiceType: {},
        durationRanges: {},
        
        // New default values
        byUrgency: {},
        highUrgencyCount: 0,
        highUrgencyRate: 0,
        atRiskCalls: [],
        atRiskCount: 0,
        byUrgencyAndStatus: {},
        serviceTypeEfficiency: {},
        peakHours: [],
        avgHandleTimeByCategory: {},
        avgHandleTimeByServiceType: {},
      }} loading={loading} />
    </div>
  );
}
